/**
 * Converts a postgres-catalog from the schema store into the schema format
 * expected by @codemirror/lang-sql's `sql()` extension.
 *
 * The catalog structure:
 * { schemas: [{ name, tables: [{ schema, name, type, columns: [{ name, dataType, nullable }], indexes }] }] }
 *
 * @codemirror/lang-sql expects:
 * schema: { [tableName: string]: string[] }  — table → column names
 * tables: [{ label, detail? }]               — for autocomplete rendering
 */

export interface PostgresCatalogColumn {
  name: string;
  dataType: string;
  nullable: boolean;
}

export interface PostgresCatalogTable {
  schema: string;
  name: string;
  type: string;
  columns: PostgresCatalogColumn[];
  indexes?: { name: string; definition: string }[];
}

export interface PostgresCatalogSchema {
  name: string;
  tables: PostgresCatalogTable[];
}

export interface PostgresCatalog {
  schemas: PostgresCatalogSchema[];
}

export interface SqlSchemaConfig {
  /** table → column names for CodeMirror SQL completion */
  schema: Record<string, readonly string[]>;
  /** All table names with detail info for completion */
  tables: { label: string; detail?: string }[];
  /** All schema names */
  schemaNames: string[];
  /** Column detail map: table.column → dataType */
  columnDetails: Map<string, string>;
}

/**
 * Convert a postgres-catalog JSON to the schema config used by CodeMirror's SQL extension.
 *
 * - Unqualified table names for the default schema (or "public" if none)
 * - Schema-qualified names for all tables (schema.table)
 * - Column names include type info for completion detail
 */
export function catalogToSqlSchema(
  catalogJson: string | null | undefined,
  defaultSchema?: string,
): SqlSchemaConfig {
  const empty: SqlSchemaConfig = { schema: {}, tables: [], schemaNames: [], columnDetails: new Map() };
  if (!catalogJson) return empty;

  let catalog: PostgresCatalog;
  try {
    catalog = JSON.parse(catalogJson) as PostgresCatalog;
  } catch {
    return empty;
  }

  if (!Array.isArray(catalog.schemas)) return empty;

  const schema: Record<string, string[]> = {};
  const tables: { label: string; detail?: string }[] = [];
  const schemaNames: string[] = [];
  const columnDetails = new Map<string, string>();
  const effectiveDefault = defaultSchema || "public";

  for (const s of catalog.schemas) {
    schemaNames.push(s.name);

    for (const table of s.tables) {
      const qualifiedName = `${s.name}.${table.name}`;
      const columns = table.columns.map((c) => c.name);

      // Always add schema-qualified name
      schema[qualifiedName] = columns;
      tables.push({
        label: qualifiedName,
        detail: table.type === "VIEW" ? "view" : "table",
      });

      // Also add unqualified name for the default schema
      if (s.name === effectiveDefault) {
        schema[table.name] = columns;
        tables.push({
          label: table.name,
          detail: table.type === "VIEW" ? "view" : "table",
        });
      }

      // Store column type details
      for (const col of table.columns) {
        const typeDetail = col.nullable ? `${col.dataType} (nullable)` : col.dataType;
        columnDetails.set(`${qualifiedName}.${col.name}`, typeDetail);
        if (s.name === effectiveDefault) {
          columnDetails.set(`${table.name}.${col.name}`, typeDetail);
        }
      }
    }
  }

  return { schema, tables, schemaNames, columnDetails };
}
